import { Box } from '@material-ui/core';
import {
  CloudUploadOutlined,
  ImageOutlined,
  PlayCircleOutlineOutlined,
  VolumeUpOutlined
} from '@material-ui/icons';
import toggleNotification from 'actions/notifications';
import { fetchPeripherals, fetchPeripheralTypes } from 'actions/peripheral';
import { createVersion, fetchVersionsForStory } from 'actions/version';
import Accordion from 'components/Accordion';
import { AoVersionEditorIframe } from 'components/AlphaOmega/VersionEditor';
import { AoVersionTransformerIframe } from 'components/AlphaOmega/VersionTransformer';
import EmptyState from 'components/EmptyState';
import i18n from 'i18n';
import { AlphaOmegaVersion } from 'interfaces/version';
import AssetManager from 'pages/stories/components/Asset/Manager';
import AssetUpload from 'pages/stories/components/Asset/Upload';
import { AssetDynamic } from 'pages/stories/interfaces/assetDynamic';
import { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

interface StoryBrowseAccordionsI {
  storyData: {
    title?: string;
    guid?: string;
    text?: string;
  };
  storyLock: boolean;
  onUpdateAssets: (asset: AssetDynamic) => void;
}

const StoryBrowseAccordions: FunctionComponent<StoryBrowseAccordionsI> = ({
  storyData,
  storyLock,
  onUpdateAssets
}: StoryBrowseAccordionsI) => {
  const dispatch = useDispatch();

  const [expanded, setExpanded] = useState<string | number | undefined>(
    'images'
  );

  const [peripherals, setPeripherals] = useState<$Lns.Peripheral[]>([]);
  const [selectedPerif, setSelectedPeripheral] = useState<$Lns.Peripheral>();
  const [isAOModalOpen, setIsAOModalOpen] = useState(false);

  const handleChange = (id: number | string, isExpanded: boolean) => {
    setExpanded(isExpanded ? id : undefined);
  };

  useEffect(() => {
    fetchPeripherals().then(({ data: pData, code: pCode }) => {
      if (pCode === 200 && pData.length > 0) {
        // filter for MAM
        fetchPeripheralTypes().then(({ data: tData, code: tCode }) => {
          if (tCode === 200 && tData.length > 0) {
            const mamType = tData.find(pin => pin.key.includes('mam'));

            const filteredData = pData.filter(peripheral => {
              return peripheral.type === mamType?.guid;
            });

            if (filteredData) {
              setPeripherals(filteredData);
              const selectedObj = filteredData.find(pin =>
                pin.name.includes('Metus')
              );
              setSelectedPeripheral(selectedObj);
            }
          }
        });
      }
    });
  }, []);

  const onSelectPeripheral = (pin: $Lns.Peripheral) => {
    setSelectedPeripheral(pin);
  };

  const onVersionSubmit = (version: AlphaOmegaVersion) => {
    createVersion({ ...version, story: storyData.guid, state: 'Draft' })
      .then(() => {
        if (storyData.guid) fetchVersionsForStory(storyData.guid);
        dispatch(
          toggleNotification(
            i18n.t('notifications.versions.createSuccess'),
            'success'
          )
        );
        setIsAOModalOpen(false);
      })
      .catch(() => {
        dispatch(toggleNotification(i18n.t('notifications.apiError'), 'error'));
      });
  };

  return (
    <Box display="grid" gridAutoFlow="row" width="100%" gridGap={16}>
      <AoVersionTransformerIframe
        version={storyData}
        submitCallback={setIsAOModalOpen}
      />
      <AoVersionEditorIframe
        show={isAOModalOpen}
        showCallback={setIsAOModalOpen}
        submitCallback={onVersionSubmit}
      />
      <Accordion
        id="images"
        icon={<ImageOutlined />}
        title={i18n.t('pages.stories.tabLabel.images')}
        expandedId={expanded}
        onChange={handleChange}
      >
        <AssetManager
          storyLock={storyLock}
          protocol={selectedPerif}
          assetType="Image"
          onUpdateAssets={onUpdateAssets}
          peripherals={peripherals}
          onSelectPeripheral={onSelectPeripheral}
        />
        {peripherals === undefined && (
          <EmptyState
            title={i18n.t('pages.stories.tabLabel.images')}
            subTitle="No content yet"
          />
        )}
      </Accordion>
      <Accordion
        title={i18n.t('pages.stories.tabLabel.videos')}
        icon={<PlayCircleOutlineOutlined />}
        expandedId={expanded}
        onChange={handleChange}
      >
        <AssetManager
          storyLock={storyLock}
          protocol={selectedPerif}
          assetType="Video"
          onUpdateAssets={onUpdateAssets}
          peripherals={peripherals}
          onSelectPeripheral={onSelectPeripheral}
        />
        {peripherals === undefined && (
          <EmptyState
            title={i18n.t('pages.stories.tabLabel.videos')}
            subTitle="No content yet"
          />
        )}
      </Accordion>
      <Accordion
        title={i18n.t('pages.stories.tabLabel.audio')}
        icon={<VolumeUpOutlined />}
        expandedId={expanded}
        onChange={handleChange}
      >
        <AssetManager
          storyLock={storyLock}
          protocol={selectedPerif}
          assetType="Audio"
          onUpdateAssets={onUpdateAssets}
          peripherals={peripherals}
          onSelectPeripheral={onSelectPeripheral}
        />
        {peripherals === undefined && (
          <EmptyState
            title={i18n.t('pages.stories.tabLabel.audio')}
            subTitle="No content yet"
          />
        )}
      </Accordion>
      <Accordion
        title={i18n.t('pages.stories.tabLabel.upload')}
        icon={<CloudUploadOutlined />}
        expandedId={expanded}
        onChange={handleChange}
      >
        <AssetUpload
          storyLock={storyLock}
          protocol={selectedPerif}
          peripheral={peripherals}
          onSelectPeripheral={onSelectPeripheral}
        />
        {peripherals === undefined && (
          <EmptyState
            title={i18n.t('pages.stories.tabLabel.images')}
            subTitle="No content yet"
          />
        )}
      </Accordion>
    </Box>
  );
};

export default StoryBrowseAccordions;
